import React from "react";
import { TrashIcon } from "@heroicons/react/solid";
import { Draggable } from "react-beautiful-dnd";
import { NormalizedChapter } from "../types";
import { extractSections, Section } from "../utils/serializers";

interface ChapterItemProps {
  chapter: NormalizedChapter;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

const SectionItem: React.FC<{ section: Section; depth: number }> = ({
  section,
  depth,
}) => {
  const marginLeft = depth * 16; // 16px per level

  return (
    <>
      <li style={{ marginLeft: `${marginLeft}px` }}>
        <div className="p-2 rounded cursor-pointer hover:bg-gray-100">
          <span className="text-sm truncate block">
            {section.number}. {section.title}
          </span>
        </div>
      </li>
      {section.children.map((child) => (
        <SectionItem key={child.id} section={child} depth={depth + 1} />
      ))}
    </>
  );
};

export const ChapterItem: React.FC<ChapterItemProps> = ({
  chapter,
  index,
  isSelected,
  onSelect,
  onDelete,
}) => {
  const sections = extractSections(chapter.content, chapter.order + 1);

  return (
    <Draggable key={chapter.id} draggableId={chapter.id} index={index}>
      {(provided) => (
        <li
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={onSelect}
        >
          {/* Chapter (H1) */}
          <div
            className={`p-2 rounded cursor-pointer transition-colors ${
              isSelected
                ? "bg-cyan-600 text-white font-semibold"
                : "hover:bg-gray-100 font-semibold"
            }`}
          >
            <div className="flex justify-between items-center gap-2 min-w-0">
              <div
                {...provided.dragHandleProps}
                className="flex items-center flex-1 min-w-0"
              >
                <div className="mr-2 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                    />
                  </svg>
                </div>
                <span className="truncate min-w-0">
                  {chapter.order + 1}. {chapter.title}
                </span>
              </div>
              <button
                onClick={onDelete}
                className="flex-shrink-0 px-2 py-1 text-xs rounded focus:text-white-600 focus:outline-none hover:text-red-500"
              >
                <TrashIcon className="flex-shrink-0 h-4" />
              </button>
            </div>
          </div>

          {/* Sections (H2/H3/H4) */}
          {sections.length > 0 && (
            <ul className="mt-1 space-y-1">
              {sections.map((section) => (
                <SectionItem key={section.id} section={section} depth={1} />
              ))}
            </ul>
          )}
        </li>
      )}
    </Draggable>
  );
};
